import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const TopFailingValidations = ({ validations = [], loading = false, days = 30 }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('failure_count');
  const [sortDirection, setSortDirection] = useState('desc');

  // Sort validations
  const sortedValidations = [...validations].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (sortField === 'last_failure') {
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4" />
    ) : (
      <ChevronDownIcon className="h-4 w-4" />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleRowClick = (validation) => {
    // Navigate to validations page with table + connection filter. The
    // connection_id is critical: this widget aggregates cross-connection,
    // so without it the destination page reads from the active-connection
    // context and shows nothing if the row was from a different connection.
    if (validation.table_name) {
      const params = new URLSearchParams();
      params.set('table', validation.table_name);
      if (validation.connection_id) {
        params.set('connection_id', validation.connection_id);
      }
      navigate(`/validations?${params.toString()}`);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Top Failing Validations</h3>
          <p className="text-sm text-gray-600 mt-1">Last {days} days</p>
        </div>
        <div className="p-2 rounded-lg bg-red-100">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
        </div>
      </div>

      {loading ? (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto overflow-y-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Validation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Table
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Failures
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Last Failure
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...Array(5)].map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="h-6 bg-gray-200 rounded-full w-12"></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : validations.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <p className="text-gray-900 font-semibold">No failing validations</p>
          <p className="text-sm text-gray-500 mt-1">All validations are passing!</p>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto overflow-y-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('validation_name')}
                  >
                    <div className="flex items-center gap-1">
                      Validation
                      <SortIcon field="validation_name" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('table_name')}
                  >
                    <div className="flex items-center gap-1">
                      Table
                      <SortIcon field="table_name" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('failure_count')}
                  >
                    <div className="flex items-center gap-1">
                      Failures
                      <SortIcon field="failure_count" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('last_failure')}
                  >
                    <div className="flex items-center gap-1">
                      Last Failure
                      <SortIcon field="last_failure" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedValidations.map((validation, idx) => (
                  <tr
                    key={validation.validation_id || idx}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(validation)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {validation.validation_name || validation.title || 'Unknown'}
                          </div>
                          {validation.affected_tables && validation.affected_tables.length > 1 && (
                            <div className="text-xs text-gray-500">
                              +{validation.affected_tables.length - 1} other tables
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {validation.table_name || 'Multiple'}
                      </div>
                      {validation.connection_name && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {validation.connection_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        validation.failure_count >= 20 ? 'bg-red-100 text-red-800' :
                        validation.failure_count >= 10 ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {validation.failure_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {formatDate(validation.last_failure)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing top <span className="font-semibold">{validations.length}</span> failing validations
              {validations.length > 0 && (
                <> with <span className="font-semibold">
                  {validations.reduce((sum, v) => sum + v.failure_count, 0)}
                </span> total failures</>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopFailingValidations;
